// Everything around the 3D canvas: playback, arch toggles, the tooth the doctor picked, and the
// change requests. One component for the lab and the clinic — the difference is only whether the
// reader may decide, which the server states on the view.
//
// The change model is deliberately Tier 1 + Tier 2:
//   Tier 1 — a note pinned to a tooth and a step ("this one is rotating too fast").
//   Tier 2 — a structured adjustment ("less torque on 23", "hold 12", "extend by 4 steps").
// There is no tier 3: dragging teeth in a browser produces a setup with no root, collision or
// biomechanical checking, which a technician has to redo in the CAD anyway. The doctor gets real
// control over the OUTCOME without being handed a tool that implies the geometry is final.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SETUP_CHANGE_KINDS,
  SETUP_CHANGE_LABELS,
  SETUP_STATUS_LABELS,
  formatDateTime,
  movingAtStep,
  summariseMovement,
  type SetupAnnotation,
  type SetupChangeKind,
  type SetupView,
  type ToothId,
  type ToothMovement,
} from '@alignova/shared';
// three.js is ~600 KB; it loads when someone actually opens a setup, not on every page of the app.
const SetupViewer = lazy(() => import('./SetupViewer.tsx').then((m) => ({ default: m.SetupViewer })));
import { SETUP_VIEWS, SETUP_VIEW_LABELS, type SetupViewAngle } from './setupViews.ts';
import { IconAlert, IconCheck, IconPause, IconPlay } from './icons.tsx';

/** Which teeth a doctor is likely to name, for the picker when nothing is clicked in 3D. */
const QUADRANT_TEETH = ['1', '2', '3', '4'].flatMap((q) => Array.from({ length: 8 }, (_, i) => `${q}${i + 1}`));

export function SetupWorkspace({
  view,
  onAnnotate,
  onDecide,
  busy,
}: {
  view: SetupView;
  /** Omitted on a share link, where the reader may look but not ask for anything. */
  onAnnotate?: (input: { toothId: string | null; stepIndex: number | null; kind: SetupChangeKind | null; note: string | null; amount: number | null }) => Promise<void>;
  onDecide?: (decision: 'approved' | 'changes_requested', comment: string) => Promise<void>;
  busy?: boolean;
}) {
  const { bundle, setup } = view;
  // Simulation revisions render in the Unity viewer, which carries its own playback, views and speed.
  const simulationSrc = view.simulationUrl ? `/unity/index.html?case=${encodeURIComponent(view.simulationUrl)}` : null;
  const lastStep = Math.max(0, (bundle?.stepCount ?? 1) - 1);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tooth, setTooth] = useState<ToothId | null>(null);
  const [hidden, setHidden] = useState<Set<'upper' | 'lower'>>(new Set());
  const [angle, setAngle] = useState<SetupViewAngle>('angle');
  const [ghost, setGhost] = useState(true);
  const raf = useRef<number | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);

  // Playback: ~1.2 steps a second, which is fast enough to read as movement and slow enough to see
  // an individual tooth travel.
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const tick = (t: number) => {
      const dt = (t - last) / 1000;
      last = t;
      setStep((s) => {
        const next = s + dt * 1.2;
        if (next >= lastStep) {
          setPlaying(false);
          return lastStep;
        }
        return next;
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [playing, lastStep]);

  const toggleArch = (arch: 'upper' | 'lower') =>
    setHidden((cur) => {
      const next = new Set(cur);
      if (next.has(arch)) next.delete(arch);
      else next.add(arch);
      return next;
    });

  const stepIndex = Math.round(step);
  const iprHere = bundle?.steps.find((s) => s.index === stepIndex)?.ipr ?? null;

  const movement = useMemo(() => (bundle ? summariseMovement(bundle) : []), [bundle]);
  // Which teeth move at the step being shown, so the eye is drawn to them during playback.
  const moving = useMemo(() => (bundle ? movingAtStep(bundle, stepIndex) : new Set<ToothId>()), [bundle, stepIndex]);
  // Attachments present by this step. They appear when they are bonded and stay for the rest.
  const attachments = useMemo(() => {
    const out = new Set<ToothId>();
    for (const st of bundle?.steps ?? []) {
      if (st.index > stepIndex) break;
      for (const t of st.attachments ?? []) out.add(t);
    }
    return out;
  }, [bundle, stepIndex]);

  const nudge = useCallback(
    (by: number) => {
      setPlaying(false);
      setStep((s) => Math.min(lastStep, Math.max(0, Math.round(s) + by)));
    },
    [lastStep],
  );

  // Arrow keys step; space plays. Scoped to the viewer so it never steals a key from a textarea.
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nudge(1);
      else if (e.key === 'ArrowLeft') nudge(-1);
      else if (e.key === ' ') setPlaying((p) => !p);
      else return;
      e.preventDefault();
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [nudge]);

  const fullscreen = () => {
    const el = stage.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.().catch(() => undefined);
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-level-1">
        {/* Canvas. Focusable so the arrow keys work, and the element that goes fullscreen. */}
        <div
          ref={stage}
          tabIndex={0}
          role="application"
          aria-label="3D treatment setup. Left and right arrows step through the plan."
          className={`relative w-full outline-none focus-visible:ring-2 focus-visible:ring-accent ${
            simulationSrc ? 'h-[72vh] min-h-[26rem] bg-black' : 'h-[52vh] min-h-[20rem] bg-navy'
          }`}
        >
          {simulationSrc ? (
            <iframe src={simulationSrc} title="Treatment simulation" allow="fullscreen" className="h-full w-full border-0" />
          ) : bundle ? (
            <Suspense fallback={<div className="grid h-full place-items-center text-sm text-white/60">Loading the viewer…</div>}>
              <SetupViewer
                bundle={bundle}
                meshUrl={view.meshUrl}
                step={step}
                selectedTooth={tooth}
                onSelectTooth={setTooth}
                hiddenArches={hidden}
                view={angle}
                ghost={ghost}
                movingTeeth={moving}
                attachments={attachments}
              />
            </Suspense>
          ) : (
            <div className="grid h-full place-items-center px-6 text-center text-sm text-white/70">
              This setup has no step data the viewer can read.
            </div>
          )}

          {/* View presets. The camera is placed in the arch's own frame, so these are the real
              clinical views whatever axis convention the CAD exported. */}
          {bundle && (
            <div className="absolute left-2 top-2 flex flex-wrap gap-1">
              {SETUP_VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAngle(v)}
                  aria-pressed={angle === v}
                  className={`tap rounded px-2 py-1 text-[11px] font-medium backdrop-blur transition ${
                    angle === v ? 'bg-white text-navy' : 'bg-black/45 text-white/85 hover:bg-black/65'
                  }`}
                >
                  {SETUP_VIEW_LABELS[v]}
                </button>
              ))}
            </div>
          )}

          {bundle && (
            <div className="absolute right-2 top-2 flex flex-wrap items-center justify-end gap-1">
              {tooth && (
                <span className="rounded bg-black/60 px-2 py-1 text-[11px] font-semibold text-white">Tooth {tooth}</span>
              )}
              <button
                type="button"
                onClick={() => setGhost((g) => !g)}
                aria-pressed={ghost}
                title="Show where the teeth start, behind where they are now"
                className={`tap rounded px-2 py-1 text-[11px] font-medium backdrop-blur transition ${
                  ghost ? 'bg-sky-400 text-navy' : 'bg-black/45 text-white/85 hover:bg-black/65'
                }`}
              >
                Start position
              </button>
              <button
                type="button"
                onClick={fullscreen}
                className="tap rounded bg-black/45 px-2 py-1 text-[11px] font-medium text-white/85 backdrop-blur hover:bg-black/65"
              >
                Fullscreen
              </button>
            </div>
          )}

          {!view.meshUrl && bundle && (
            <div className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-black/50 px-2.5 py-1.5 text-[11px] text-white/80">
              Schematic - tooth positions only. The 3D model hasn&rsquo;t been attached to this revision.
            </div>
          )}
        </div>

        {simulationSrc && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-3 py-2 text-xs text-muted">
            <span>{setup.stepCount - 1} stages · per-stage models from the Alignova Simulation Builder</span>
            <a href={simulationSrc} target="_blank" rel="noopener" className="font-medium text-accent hover:underline">
              Open full screen
            </a>
          </div>
        )}

        {/* Playback (three.js setups; the simulation viewer has its own) */}
        {!simulationSrc && (
          <div className="flex flex-wrap items-center gap-3 border-t border-line px-3 py-2.5">
            <button
              type="button"
              onClick={() => {
                if (step >= lastStep) setStep(0);
                setPlaying((p) => !p);
              }}
              aria-label={playing ? 'Pause' : 'Play'}
              className="tap grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-accent-fg"
            >
              {playing ? <IconPause size={18} /> : <IconPlay size={18} />}
            </button>

            <button
              type="button"
              onClick={() => nudge(-1)}
              aria-label="Previous step"
              className="tap grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-2 text-ink"
            >
              &lsaquo;
            </button>

            <input
              type="range"
              min={0}
              max={lastStep}
              step={0.01}
              value={step}
              onChange={(e) => {
                setPlaying(false);
                setStep(Number(e.target.value));
              }}
              aria-label="Treatment step"
              className="h-1.5 min-w-[10rem] flex-1 cursor-pointer accent-[var(--c-accent)]"
            />

            <button
              type="button"
              onClick={() => nudge(1)}
              aria-label="Next step"
              className="tap grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-2 text-ink"
            >
              &rsaquo;
            </button>

            <span className="tabnum shrink-0 text-sm font-medium text-ink">
              Step {stepIndex}
              <span className="text-muted"> / {lastStep}</span>
              {moving.size > 0 && <span className="ml-1.5 text-xs font-normal text-status-warn-fg">{moving.size} moving</span>}
            </span>

            <span className="flex shrink-0 gap-1">
              {(['upper', 'lower'] as const).map((arch) => (
                <button
                  key={arch}
                  type="button"
                  onClick={() => toggleArch(arch)}
                  className={`tap rounded-md px-2.5 py-1.5 text-xs font-medium capitalize transition ${
                    hidden.has(arch) ? 'bg-surface-2 text-muted' : 'bg-primary text-primary-fg'
                  }`}
                >
                  {arch}
                </button>
              ))}
            </span>
          </div>
        )}

        {iprHere && Object.keys(iprHere).length > 0 && (
          <div className="border-t border-line bg-surface-2/60 px-3 py-2 text-xs text-muted">
            <b className="text-ink">IPR at this step:</b>{' '}
            {Object.entries(iprHere)
              .map(([pair, mm]) => `${pair} ${mm}mm`)
              .join(' · ')}
          </div>
        )}
      </div>

      {movement.length > 0 && (
        <MovementTable rows={movement} selected={tooth} onSelect={setTooth} />
      )}

      {/* Requests */}
      {onAnnotate ? (
        <RequestPanel
          view={view}
          tooth={tooth}
          stepIndex={stepIndex}
          onPickTooth={setTooth}
          onAnnotate={onAnnotate}
          busy={busy}
        />
      ) : (
        view.annotations.length > 0 && (
          <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
            <h2 className="mb-2 text-sm font-semibold text-ink">Notes from the lab</h2>
            <ul className="space-y-2">
              {view.annotations.map((a) => (
                <AnnotationRow key={a.id} a={a} />
              ))}
            </ul>
          </section>
        )
      )}

      {onDecide && setup.status === 'shared' && <DecisionPanel onDecide={onDecide} busy={busy} />}

      {setup.status !== 'shared' && (
        <p className="text-xs text-muted">
          This revision is <b className="text-ink">{SETUP_STATUS_LABELS[setup.status].toLowerCase()}</b>
          {setup.decidedAt ? ` - ${formatDateTime(setup.decidedAt)}${setup.decidedByName ? ` by ${setup.decidedByName}` : ''}` : ''}.
        </p>
      )}
    </div>
  );
}

function RequestPanel({
  view,
  tooth,
  stepIndex,
  onPickTooth,
  onAnnotate,
  busy,
}: {
  view: SetupView;
  tooth: ToothId | null;
  stepIndex: number;
  onPickTooth: (t: ToothId | null) => void;
  onAnnotate: (input: { toothId: string | null; stepIndex: number | null; kind: SetupChangeKind | null; note: string | null; amount: number | null }) => Promise<void>;
  busy?: boolean;
}) {
  const [kind, setKind] = useState<SetupChangeKind | ''>('');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const send = async () => {
    if (!kind && !note.trim()) {
      setErr('Pick a change or write a note.');
      return;
    }
    setErr(null);
    await onAnnotate({
      toothId: tooth,
      stepIndex,
      kind: kind || null,
      note: note.trim() || null,
      amount: amount ? Number(amount) : null,
    });
    setKind('');
    setNote('');
    setAmount('');
  };

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <h2 className="text-sm font-semibold text-ink">Ask for a change</h2>
      <p className="mb-3 mt-0.5 text-xs text-muted">
        Click a tooth in the model, or pick one below. The lab applies changes in the design software
        and sends a new revision - nothing here moves the teeth directly.
      </p>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Tooth</span>
          <select
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm"
            value={tooth ?? ''}
            onChange={(e) => onPickTooth(e.target.value || null)}
          >
            <option value="">Whole case</option>
            {QUADRANT_TEETH.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <span className="tabnum text-xs text-muted">at step {stepIndex}</span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {SETUP_CHANGE_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(kind === k ? '' : k)}
              className={`tap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                kind === k ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-ink hover:bg-line'
              }`}
            >
              {SETUP_CHANGE_LABELS[k]}
            </button>
          ))}
        </div>

        {(kind === 'more_ipr' || kind === 'less_ipr' || kind === 'extend_steps' || kind === 'shorten_steps') && (
          <input
            type="number"
            step="0.1"
            className="h-9 w-40 rounded-md border border-line bg-surface px-3 text-sm"
            placeholder={kind.includes('ipr') ? 'mm' : 'steps'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        )}

        <textarea
          className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm"
          rows={2}
          placeholder="Anything the technician should know"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {err && <p className="text-sm text-status-urgent-fg">{err}</p>}

        <button
          type="button"
          onClick={() => void send()}
          disabled={busy}
          className="tap rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-fg disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Add this request'}
        </button>
      </div>

      {view.annotations.length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-line pt-3">
          {view.annotations.map((a) => (
            <AnnotationRow key={a.id} a={a} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AnnotationRow({ a }: { a: SetupAnnotation }) {
  return (
    <li className="rounded-md border border-line px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {a.toothId && (
          <span className="tabnum rounded bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink">
            Tooth {a.toothId}
          </span>
        )}
        {a.kind && <span className="font-medium text-ink">{SETUP_CHANGE_LABELS[a.kind]}</span>}
        {a.amount != null && <span className="tabnum text-xs text-muted">{a.amount}</span>}
        {a.stepIndex != null && <span className="tabnum text-xs text-muted">step {a.stepIndex}</span>}
        <span className="ml-auto text-[11px] text-muted">
          {a.authorSide === 'lab' ? 'Lab' : 'Doctor'} · {formatDateTime(a.createdAt)}
        </span>
      </div>
      {a.note && <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{a.note}</p>}
      {a.resolvedAt && (
        <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-status-progress-fg">
          <IconCheck size={13} /> Applied
        </p>
      )}
    </li>
  );
}

function DecisionPanel({
  onDecide,
  busy,
}: {
  onDecide: (decision: 'approved' | 'changes_requested', comment: string) => Promise<void>;
  busy?: boolean;
}) {
  const [comment, setComment] = useState('');
  return (
    <section className="rounded-lg border border-status-warn-fg/30 bg-status-warn-bg/50 p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-status-warn-fg">
        <IconAlert size={18} />
        Your decision on this setup
      </h2>
      <p className="mb-3 mt-0.5 text-xs text-status-warn-fg/90">
        Approving moves the case into production. Requesting changes sends it back with everything you
        marked above.
      </p>
      <textarea
        className="w-full rounded-md border border-status-warn-fg/30 bg-surface px-3 py-2 text-sm"
        rows={2}
        placeholder="Optional comment for the lab"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onDecide('approved', comment)}
          className="tap rounded-md bg-status-progress-fg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Approve this setup'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void onDecide('changes_requested', comment)}
          className="tap rounded-md border border-status-warn-fg/40 bg-surface px-5 py-2.5 text-sm font-semibold text-status-warn-fg disabled:opacity-50"
        >
          Request changes
        </button>
      </div>
    </section>
  );
}

/**
 * What the plan actually does, per tooth.
 *
 * An animation shows movement but doesn't quantify it, and "is 3.2 mm of distalisation realistic on
 * this patient" is the question a doctor is being asked to answer. Sorted by distance so the teeth
 * being asked to do the most work are the first thing read — those are where a plan goes wrong.
 */
function MovementTable({
  rows,
  selected,
  onSelect,
}: {
  rows: ToothMovement[];
  selected: ToothId | null;
  onSelect: (t: ToothId | null) => void;
}) {
  const [all, setAll] = useState(false);
  // A tooth that shifts less than a tenth of a millimetre is holding position, not moving.
  const active = rows.filter((r) => r.translationMm >= 0.1 || r.rotationDeg >= 1 || r.iprMm > 0);
  const sorted = [...active].sort((a, b) => b.translationMm - a.translationMm || b.rotationDeg - a.rotationDeg);
  const shown = all ? sorted : sorted.slice(0, 8);

  if (!sorted.length) {
    return (
      <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
        <h2 className="text-sm font-semibold text-ink">Planned movement</h2>
        <p className="mt-1 text-sm text-muted">
          No tooth in this plan moves more than a tenth of a millimetre - check the import before
          sending it out.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Planned movement</h2>
        <p className="text-xs text-muted">
          {active.length} of {rows.length} teeth move · start to finish
        </p>
      </div>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[30rem] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="py-1.5 pr-2 font-medium">Tooth</th>
              <th className="py-1.5 pr-2 text-right font-medium">Distance</th>
              <th className="py-1.5 pr-2 text-right font-medium">Rotation</th>
              <th className="py-1.5 pr-2 text-right font-medium">IPR</th>
              <th className="py-1.5 pr-2 text-right font-medium">Steps</th>
              <th className="py-1.5 font-medium">Attachment</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={r.toothId}
                onClick={() => onSelect(selected === r.toothId ? null : r.toothId)}
                className={`cursor-pointer border-b border-line/60 transition last:border-0 ${
                  selected === r.toothId ? 'bg-accent/10' : 'hover:bg-surface-2'
                }`}
              >
                <td className="py-1.5 pr-2">
                  <b className="tabnum text-ink">{r.toothId}</b>
                  <span className="ml-1.5 text-xs text-muted">{r.arch}</span>
                </td>
                <td className="tabnum py-1.5 pr-2 text-right text-ink">{r.translationMm.toFixed(2)} mm</td>
                <td className="tabnum py-1.5 pr-2 text-right text-ink">{r.rotationDeg.toFixed(1)}°</td>
                <td className="tabnum py-1.5 pr-2 text-right text-muted">{r.iprMm ? `${r.iprMm.toFixed(2)} mm` : '-'}</td>
                <td className="tabnum py-1.5 pr-2 text-right text-muted">{r.activeSteps.length || '-'}</td>
                <td className="py-1.5 text-muted">{r.hasAttachment ? 'Yes' : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sorted.length > 8 && (
        <button type="button" onClick={() => setAll((a) => !a)} className="tap mt-2 text-sm font-medium text-accent">
          {all ? 'Show the busiest 8' : `Show all ${sorted.length}`}
        </button>
      )}
    </section>
  );
}
